import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Ink from 'react-ink';

const ClaimWelcomeModal = ({ isOpen, onClose, advisorName, agencyName, consortiumName, scrollToId }) => {
  const handleClose = useCallback(() => {
    onClose();
    if (scrollToId) {
      setTimeout(() => {
        const el = document.getElementById(scrollToId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [onClose, scrollToId]);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[560px] mx-4 relative">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="p-8">
          <h2
            className="text-[32px] font-semibold leading-[42px] text-black mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="claim-modal-heading"
          >
            Welcome, {advisorName}!
          </h2>
          <p
            className="text-[18px] font-normal leading-[28px] text-black mb-2"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            We found your travel advisor profile.
          </p>
          {consortiumName && (
            <p
              className="text-[16px] font-normal leading-[24px] text-[#6E6E6E] mb-1"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Consortium: {consortiumName}
            </p>
          )}
          {agencyName && (
            <p
              className="text-[16px] font-normal leading-[24px] text-[#6E6E6E] mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Agency: {agencyName}
            </p>
          )}
          <p
            className="text-[16px] font-normal leading-[24px] text-black mb-8"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Complete the registration below to claim your account and start using Ship&Play.
          </p>

          <button
            type="button"
            onClick={handleClose}
            data-testid="claim-modal-continue"
            className="w-full px-8 py-3 text-white font-semibold text-[16px] leading-[20px]
              bg-[#49C44F] rounded-lg relative hover:bg-[#3fa841]"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Continue
            <Ink />
          </button>
        </div>
      </div>
    </div>
  );
};

ClaimWelcomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  advisorName: PropTypes.string.isRequired,
  agencyName: PropTypes.string,
  consortiumName: PropTypes.string,
  scrollToId: PropTypes.string
};

export default ClaimWelcomeModal;
